import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useLazyCodeCheckQuery } from 'services/signupService';
import { isFetchBaseQueryError } from 'utils';
import { useAppDispatch } from 'store/store';
import { CodeCheckApiResponse, AccessRestrictionError } from 'features/signUp/types';
import { setSignUpState } from 'features/signUp/signUpSlice';
import { setOidcConfig } from 'features/auth/authSlice';
import { NOTIFICATIONS_FEATURE_FLAG } from 'features/settings/constants';
import { ErrorCodes } from 'features/auth/constants';
import { SIGNUP_ERROR_MESSAGE } from 'features/signUp/constants';
import { SignupError } from '../useSignupError/SignupError';

const handleError = (error: FetchBaseQueryError | SerializedError) => {
  if (isFetchBaseQueryError(error)) {
    if (error.status === 'FETCH_ERROR') {
      throw new SignupError('Network error occurred while validating access code.', 'NoNetwork');
    }

    // Handle access restriction - backend returns 403 with APPLICATION_FORBIDDEN error_code
    if (error.status === 403) {
      const errorData = error?.data as AccessRestrictionError;
      // Only throw AccessRestricted if error_code is APPLICATION_FORBIDDEN
      if (errorData?.error_code === ErrorCodes.APPLICATION_FORBIDDEN) {
        throw new SignupError(SIGNUP_ERROR_MESSAGE.AccessRestricted, 'AccessRestricted');
      }
    }
  }

  throw new SignupError('An unknown error occurred while validating access code.', 'Generic');
};

export const useAccessCodeStep = () => {
  const [codeCheck, { isLoading: isSubmitting }] = useLazyCodeCheckQuery();
  const appDispatch = useAppDispatch();

  const updateStore = (codeCheckData: CodeCheckApiResponse) => {
    appDispatch(
      setSignUpState({
        isUserInvitedCaregiver: codeCheckData.has_caregiver,
        isHospitalCaregiverEnabled: codeCheckData.hospital.caregivers_enabled,
        isEligibilityCheckRequired: codeCheckData.requires_eligibility,
        eligibilityCheckMethod:
          codeCheckData.eligibility_field_name === 'Member ID' ? 'memberId' : 'lastName',
        isHospitalSsoEnabled: Boolean(codeCheckData.authConfig),
        caregiverRelationships: codeCheckData.hospital.caregiver_relationships,
        hospitalID: codeCheckData.hospital.id.toString(),
        userType: codeCheckData.has_caregiver ? 'caregiver' : 'member',
        privacyPolicy: codeCheckData.privacy_policy,
        eulaPolicy: codeCheckData.eula_policy,
        hospitalDisplayName: codeCheckData.hospital.display_name,
        eligibilityFieldPhoto: codeCheckData?.eligibility_field_photo,
        eligibilityMessage: codeCheckData?.eligibility_message,
        notificationFlag: codeCheckData.feature_flags?.find(
          (flag) => flag.feature_flag_type === NOTIFICATIONS_FEATURE_FLAG.CHAT_SMS,
        ),
        isHospitalSmsEnabled: codeCheckData.hospital.member_sms_enabled,
        optInMessage: codeCheckData.hospital.opt_in_message,
      }),
    );
    appDispatch(setOidcConfig(codeCheckData.authConfig || null));
  };

  const submit = async (accessCode: string) => {
    try {
      const { data, error } = await codeCheck(accessCode);

      if (error) {
        handleError(error);
      }

      if (!data) {
        throw new SignupError('An unknown error occurred while validating access code.', 'Generic');
      }

      if (!data.valid) {
        throw new SignupError('The provided access code is invalid.', 'InvalidAccessCode');
      }

      updateStore(data);
    } catch (error) {
      if (error instanceof SignupError) {
        throw error;
      }

      throw new SignupError('An unknown error occurred while validating access code.', 'Generic');
    }
  };

  return { isSubmitting, submit, updateStore };
};
